import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, FormLabel, Input, Select, Stack, Text, Textarea, useToast } from '@chakra-ui/react';
import { CallbackRequestsAPI, ContactsAPI, TeamAPI } from '../../services/api';
import { followupCopy } from './callbackExperience';

const personLabel = (name, email) => (name && email && name !== email ? `${name} (${email})` : name || email || '');

// Owner + active sub-accounts and staff in the directory. Each source may be
// unavailable (e.g. a sub-account cannot list the team); the field stays free text.
export const loadAssigneeOptions = async () => {
  const [me, team, contacts] = await Promise.allSettled([TeamAPI.me(), TeamAPI.members(), ContactsAPI.list()]);
  const teamOptions = [];
  if (me.status === 'fulfilled' && me.value?.is_owner && me.value.email) teamOptions.push(me.value.email);
  if (team.status === 'fulfilled') {
    for (const member of team.value?.items || []) {
      if (member.is_active === false) continue;
      const label = personLabel(member.display_name, member.email);
      if (label) teamOptions.push(label);
    }
  }
  const directoryOptions = contacts.status === 'fulfilled' && Array.isArray(contacts.value)
    ? contacts.value.filter(contact => contact.contact_type === 'employee' && contact.name)
      .map(contact => (contact.extension ? `${contact.name} (${contact.extension})` : contact.name))
    : [];
  const unique = list => [...new Set(list.map(value => value.slice(0, 200)))];
  return { team: unique(teamOptions), directory: unique(directoryOptions) };
};

export default function CallbackFollowup({ ticket, lang, statusLabels, onSaved }) {
  const copy = followupCopy(lang);
  const [assignee, setAssignee] = useState(ticket.assignee || '');
  const [result, setResult] = useState('');
  const [nextAction, setNextAction] = useState(ticket.next_action || '');
  const [status, setStatus] = useState(ticket.status);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState({ team: [], directory: [] });
  const toast = useToast();
  useEffect(() => {
    let live = true;
    loadAssigneeOptions().then(value => live && setOptions(value)).catch(() => {});
    return () => { live = false; };
  }, []);
  const save = async () => {
    if (status === 'completed' && !result.trim()) {
      toast({ title: copy.resultRequired, status: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const updated = await CallbackRequestsAPI.updateFollowup(ticket.id, {
        new_status: status, assignee: assignee.trim(),
        notes: result.trim() || undefined, next_action: nextAction.trim(),
      });
      setResult('');
      onSaved(updated);
      toast({ title: copy.saved, status: 'success' });
    } catch {
      toast({ title: copy.error, status: 'error' });
    } finally { setSaving(false); }
  };
  const hasOptions = options.team.length > 0 || options.directory.length > 0;
  return <Stack spacing={4}>
    <Text fontWeight="bold">{copy.history}</Text>
    {!ticket.followup_history?.length && <Text color="gray.600">{ticket.notes || copy.empty}</Text>}
    {(ticket.followup_history || []).map((entry, index) => <Box key={`${entry.at}-${index}`} borderLeft="3px solid" borderColor="blue.200" pl={3}>
      <Text fontSize="sm" color="gray.600">{new Date(entry.at).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US')} · {entry.actor} · {statusLabels[entry.status] || entry.status}</Text>
      <Text>{copy.assignee}: {entry.assignee || copy.unassigned}</Text>
      {entry.result && <Text whiteSpace="pre-wrap">{entry.result}</Text>}
      {entry.next_action && <Text whiteSpace="pre-wrap">{copy.next}: {entry.next_action}</Text>}
    </Box>)}
    <Box>
      <FormControl><FormLabel>{copy.assignee}</FormLabel><Input maxLength={200} value={assignee} onChange={e => setAssignee(e.target.value)} /></FormControl>
      {hasOptions && <Select mt={2} size="sm" aria-label={copy.pickAssignee} value="" onChange={e => e.target.value && setAssignee(e.target.value)}>
        <option value="">{copy.pickAssignee}</option>
        {options.team.length > 0 && <optgroup label={copy.teamMembers}>{options.team.map(value => <option key={`t-${value}`} value={value}>{value}</option>)}</optgroup>}
        {options.directory.length > 0 && <optgroup label={copy.directory}>{options.directory.map(value => <option key={`d-${value}`} value={value}>{value}</option>)}</optgroup>}
      </Select>}
    </Box>
    <FormControl><FormLabel>{copy.result}</FormLabel><Textarea maxLength={4000} value={result} onChange={e => setResult(e.target.value)} /></FormControl>
    <FormControl><FormLabel>{copy.next}</FormLabel><Textarea maxLength={2000} value={nextAction} onChange={e => setNextAction(e.target.value)} /></FormControl>
    <FormControl><FormLabel>{copy.status}</FormLabel><Select value={status} onChange={e => setStatus(e.target.value)}>{Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}</Select></FormControl>
    <Button colorScheme="blue" isLoading={saving} onClick={save}>{copy.save}</Button>
  </Stack>;
}
